import { isResource } from '@medplum/core'
import type {
  CodeableConcept,
  ImmunizationRecommendation,
  Patient,
  Reference,
} from '@medplum/fhirtypes'
import { isAfter, parseISO } from 'date-fns'
import { getVaccineFamilyFromCVX } from '#src/cdc/vaccines/cvx-mapping.ts'
import {
  type CodingValue,
  getCodingFromCodeableConcept,
  hasCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  CVX_SYSTEM,
  immunizationRecommendationStatusComplete,
} from '#src/codeSystems.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import type { ExtendedImmunization } from '#src/models/immunizations/ExtendedImmunization.ts'
import {
  DATE_VACCINE_DUE_CODING,
  EARLIEST_ADMINISTRATION_DATE_CODING,
} from '#src/models/loinc.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export class ExtendedImmunizationRecommendation extends ExtendedResource<ImmunizationRecommendation> {
  static readonly RESOURCE_TYPE = 'ImmunizationRecommendation'

  static isInstance(value: unknown): value is ImmunizationRecommendation {
    return isResource(value) && isOfType(value, 'ImmunizationRecommendation')
  }

  static byParticipant(participant: Reference<Patient>): QueryTypes {
    return {
      patient: participant,
    }
  }

  static byParticipantAndVaccineCode(
    participant: Reference<Patient>,
    vaccineCode: CodingValue
  ): QueryTypes {
    return {
      ...this.byParticipant(participant),
      'vaccine-type': vaccineCode,
    }
  }

  get patient(): Reference<Patient> {
    return this.resource.patient
  }

  get vaccineCodes(): CodeableConcept[] | undefined {
    return this.resource.recommendation?.[0]?.vaccineCode
  }

  get dateDue(): string | undefined {
    return this.resource.recommendation?.[0]?.dateCriterion?.find((dc) =>
      hasCodeableConcept(dc.code, DATE_VACCINE_DUE_CODING)
    )?.value
  }

  get dateEarliest(): string | undefined {
    return this.resource.recommendation?.[0]?.dateCriterion?.find((dc) =>
      hasCodeableConcept(dc.code, EARLIEST_ADMINISTRATION_DATE_CODING)
    )?.value
  }

  get firstNonCompletedRecommendation():
    | ImmunizationRecommendation['recommendation'][0]
    | undefined {
    return this.resource.recommendation?.find(
      (rec) =>
        !hasCodeableConcept(
          rec.forecastStatus,
          immunizationRecommendationStatusComplete
        ) && !rec.supportingImmunization?.length
    )
  }

  get firstNonCompletedDateDue(): string | undefined {
    const rec = this.firstNonCompletedRecommendation
    return rec?.dateCriterion?.find((dc) =>
      hasCodeableConcept(dc.code, DATE_VACCINE_DUE_CODING)
    )?.value
  }

  get firstNonCompletedDateEarliest(): string | undefined {
    const rec = this.firstNonCompletedRecommendation
    return rec?.dateCriterion?.find((dc) =>
      hasCodeableConcept(dc.code, EARLIEST_ADMINISTRATION_DATE_CODING)
    )?.value
  }

  get hasNonCompletedRecommendations(): boolean {
    return this.firstNonCompletedRecommendation !== undefined
  }

  mergeWithCairData(
    cairRecommendation: ExtendedImmunizationRecommendation
  ): void {
    const completedRecommendations = this.resource.recommendation.filter(
      (rec) =>
        hasCodeableConcept(
          rec.forecastStatus,
          immunizationRecommendationStatusComplete
        ) || rec.supportingImmunization?.length
    )

    const newRecommendations = cairRecommendation.resource.recommendation

    /* Deduplicate CAIR recommendations by vaccine code and dates.
     * For each unique vaccine code, keep only the recommendation with the most recent dates.
     */
    const deduplicatedNewRecs = newRecommendations.reduce(
      (acc, rec) => {
        const vaccineCode = rec.vaccineCode?.[0]?.coding?.[0]
        if (!vaccineCode?.code) {
          acc.push(rec)
          return acc
        }

        const dateDue =
          rec.dateCriterion?.find((dc) =>
            hasCodeableConcept(dc.code, DATE_VACCINE_DUE_CODING)
          )?.value ?? null
        const dateEarliest =
          rec.dateCriterion?.find((dc) =>
            hasCodeableConcept(dc.code, EARLIEST_ADMINISTRATION_DATE_CODING)
          )?.value ?? null

        const key = `${vaccineCode.code}|${dateDue ?? ''}|${dateEarliest ?? ''}`

        const existingIndex = acc.findIndex((existing) => {
          const existingVaccineCode = existing.vaccineCode?.[0]?.coding?.[0]
          if (
            !existingVaccineCode?.code ||
            existingVaccineCode.code !== vaccineCode.code
          ) {
            return false
          }

          const existingDateDue =
            existing.dateCriterion?.find((dc) =>
              hasCodeableConcept(dc.code, DATE_VACCINE_DUE_CODING)
            )?.value ?? null
          const existingDateEarliest =
            existing.dateCriterion?.find((dc) =>
              hasCodeableConcept(dc.code, EARLIEST_ADMINISTRATION_DATE_CODING)
            )?.value ?? null

          return (
            `${existingVaccineCode.code}|${existingDateDue ?? ''}|${existingDateEarliest ?? ''}` ===
            key
          )
        })

        if (existingIndex === -1) {
          acc.push(rec)
        }
        /* If existingIndex !== -1, we already have this recommendation, skip the duplicate */

        return acc
      },
      [] as typeof newRecommendations
    )

    this.resource.recommendation = [
      ...completedRecommendations,
      ...deduplicatedNewRecs,
    ]

    if (cairRecommendation.resource.date) {
      this.resource.date = cairRecommendation.resource.date
    }
  }

  maybeAddressWith(immunization: ExtendedImmunization): boolean {
    // Only completed immunizations should mark recommendations as complete
    // Declined immunizations (status: not-done) should not complete recommendations
    if (!immunization.isCompleted) {
      return false
    }

    const recommendationCVX = getCodingFromCodeableConcept(
      this.vaccineCodes,
      CVX_SYSTEM
    )
    if (!recommendationCVX?.code) {
      return false
    }
    const recommendationFamily = getVaccineFamilyFromCVX(recommendationCVX.code)

    const immunizationCVX = getCodingFromCodeableConcept(
      immunization.vaccineCode,
      CVX_SYSTEM
    )
    if (!immunizationCVX?.code) {
      return false
    }
    const immunizationFamily = getVaccineFamilyFromCVX(immunizationCVX.code)
    const sameFamily =
      immunizationFamily && recommendationFamily === immunizationFamily
    const sameCode = immunizationCVX.code === recommendationCVX.code
    // The vaccine family check is not comprehensive (only CDC adult recommendations are covered), so we allow same code to address the recommendation
    if (!sameFamily && !sameCode) {
      return false
    }

    const administeredDate = immunization.administeredDate
    if (!administeredDate) {
      return false
    }

    for (const rec of this.resource.recommendation) {
      if (
        hasCodeableConcept(
          rec.forecastStatus,
          immunizationRecommendationStatusComplete
        )
      ) {
        continue
      }
      if (rec.supportingImmunization?.length) {
        continue
      }

      const dateCriteria = rec.dateCriterion ?? []
      if (dateCriteria.length) {
        const earliestDate = dateCriteria.find((dc) =>
          hasCodeableConcept(dc.code, EARLIEST_ADMINISTRATION_DATE_CODING)
        )?.value

        if (earliestDate) {
          const administeredDateObj = parseISO(administeredDate)
          const earliestDateObj = parseISO(earliestDate)
          if (isAfter(earliestDateObj, administeredDateObj)) {
            continue
          }
        }
      }

      rec.forecastStatus = immunizationRecommendationStatusComplete
      rec.supportingImmunization ??= []
      rec.supportingImmunization.push(immunization.reference)
      return true
    }

    return false
  }
}
